/**
 * GovernmentIntakePage — `/government/intake`.
 *
 * Counter-clerk intake flow:
 *   1. Pick service from catalogue.
 *   2. Capture citizen + service inputs.
 *   3. POST /v1/government/requests creates draft.
 *   4. POST /v1/government/requests/:id/counter-pay records counter
 *      payment (CASH / POS / TRANSFER) and submits the draft to the
 *      partner gateway in one call.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  counterPayServiceRequest,
  createServiceRequest,
  listGovernmentServices,
  type CounterPayMethod,
  type ServiceRequest,
} from '../../lib/api/government';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

const PAYMENT_METHODS: CounterPayMethod[] = ['CASH', 'POS', 'TRANSFER'];

export function GovernmentIntakePage(): JSX.Element {
  const services = useQuery({
    queryKey: ['gov', 'intake', 'services'],
    queryFn: () => listGovernmentServices({ activeOnly: true, pageSize: 100 }),
    retry: false,
  });

  const [serviceCode, setServiceCode] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [nin, setNin] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<CounterPayMethod>('CASH');
  const [paymentRef, setPaymentRef] = useState('');
  const [draft, setDraft] = useState<ServiceRequest | null>(null);
  const [submitted, setSubmitted] = useState<ServiceRequest | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createServiceRequest({
        serviceCode,
        ...(customerId.trim().length > 0 ? { customerId: customerId.trim() } : {}),
        identityRef: nin.trim(),
        inputs: {
          nin: nin.trim(),
          fullName: fullName.trim(),
          phone: phone.trim(),
        },
      }),
    onSuccess: (row) => {
      setDraft(row);
      setSubmitted(null);
      setErr(null);
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setErr('Permission required: Government/createRequest');
      } else {
        setErr((e as Error).message);
      }
    },
  });

  const counterPay = useMutation({
    mutationFn: (id: string) =>
      counterPayServiceRequest(id, {
        method: paymentMethod,
        ...(paymentRef.trim() ? { reference: paymentRef.trim() } : {}),
      }),
    onSuccess: (row) => {
      setSubmitted(row);
      setErr(null);
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setErr('Permission required: Government/counterPay');
      } else {
        setErr((e as Error).message);
      }
    },
  });

  const canSubmit =
    serviceCode.length > 0 && nin.length === 11 && fullName.trim().length > 0 && phone.trim().length > 0;

  return (
    <div className="space-y-6">
      <PageHeader title="Process government service" description="Citizen intake → draft → counter payment → partner submit." />

      <div className="bg-white border border-border rounded-xl p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Service</label>
          {services.isPending ? (
            <p className="text-sm text-muted">Loading services…</p>
          ) : services.isError ? (
            <p className="text-sm text-danger">Catalogue unavailable. Try later.</p>
          ) : (
            <select
              data-testid="intake-service"
              value={serviceCode}
              onChange={(e) => setServiceCode(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            >
              <option value="">Choose a service…</option>
              {services.data?.items.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.displayName} ({s.agency}) — NGN {s.agencyFee}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Customer ID (optional)</label>
            <input
              data-testid="intake-customer-id"
              type="text"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="cust-…"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">NIN (11 digits)</label>
            <input
              data-testid="intake-nin"
              type="text"
              value={nin}
              onChange={(e) => setNin(e.target.value.replace(/\D/g, '').slice(0, 11))}
              maxLength={11}
              placeholder="11-digit NIN"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Full name</label>
            <input
              data-testid="intake-fullname"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Phone</label>
            <input
              data-testid="intake-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+234…"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
        </div>

        <button
          type="button"
          data-testid="intake-create-draft"
          onClick={() => create.mutate()}
          disabled={!canSubmit || create.isPending}
          className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-nipost-green-dark"
        >
          {create.isPending ? 'Creating draft…' : 'Create draft'}
        </button>
        {err ? <p className="text-xs text-danger" data-testid="intake-error">{err}</p> : null}
      </div>

      {draft ? (
        <div className="bg-white border border-border rounded-xl p-5 space-y-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Draft created</p>
            <div className="flex items-center gap-3 mt-1">
              <span className="font-mono text-xs" data-testid="draft-request-number">{draft.requestNumber}</span>
              <StatusBadge status={submitted?.status ?? draft.status} />
            </div>
            <p className="text-xs text-muted">Service: {draft.serviceCode} · Agency: {draft.agency}</p>
          </div>

          {!submitted ? (
            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-sm font-medium text-gray-900">Capture counter payment</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted mb-1">Method</label>
                  <select
                    data-testid="counter-method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value as CounterPayMethod)}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                  >
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted mb-1">POS / receipt reference (optional)</label>
                  <input
                    data-testid="counter-reference"
                    type="text"
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    placeholder="POS-12345"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
                  />
                </div>
              </div>
              <button
                type="button"
                data-testid="counter-submit"
                onClick={() => counterPay.mutate(draft.id)}
                disabled={counterPay.isPending}
                className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50 hover:bg-nipost-green-dark"
              >
                {counterPay.isPending ? 'Submitting to partner…' : 'Capture payment & submit'}
              </button>
            </div>
          ) : (
            <div className="border-t border-border pt-4">
              <p className="text-sm font-medium text-green-700" data-testid="counter-success">
                Submitted. Status: {submitted.status}
              </p>
              {submitted.partnerRef ? (
                <p className="text-xs text-muted mt-1">Partner ref: <span className="font-mono">{submitted.partnerRef}</span></p>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
